import type { EmotionCache } from "@emotion/cache";
import { CacheProvider } from "@emotion/react";
import CssBaseline from "@mui/material/CssBaseline";
import { ThemeProvider } from "@mui/material/styles";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import type { NextPage } from "next";
import type { AppProps } from "next/app";
import Head from "next/head";
import Router, { useRouter } from "next/router";
import Script from "next/script";
import nProgress from "nprogress";
import type { FC } from "react";
import { useEffect } from "react";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { Toaster } from "react-hot-toast";
import { Provider as ReduxProvider } from "react-redux";
import { CustomWindow } from "src/components/pricing/pricing-plan";
import SnackbarProvider from "src/components/snackbarProvider";
import { RTL } from "../components/rtl";
import { SplashScreen } from "../components/splash-screen";
import { gtmConfig } from "../config";
import { AuthConsumer, AuthProvider } from "../contexts/amplify-context";
import {
  SettingsConsumer,
  SettingsProvider,
} from "../contexts/settings-context";
import "../i18n";
import { gtm } from "../lib/gtm";
import { store } from "../store";
import { createTheme } from "../theme";
import { createEmotionCache } from "../utils/create-emotion-cache";
import "/node_modules/flag-icons/css/flag-icons.min.css";
import "../styles/bulk-briefs-grid.css";

type EnhancedAppProps = AppProps & {
  Component: NextPage;
  emotionCache: EmotionCache;
};

declare let window: CustomWindow;

Router.events.on("routeChangeStart", nProgress.start);
Router.events.on("routeChangeError", nProgress.done);
Router.events.on("routeChangeComplete", nProgress.done);

const clientSideEmotionCache = createEmotionCache();

const App: FC<EnhancedAppProps> = (props) => {
  const { Component, emotionCache = clientSideEmotionCache, pageProps } = props;

  const getLayout = Component.getLayout ?? ((page) => page);

  const isProduction = process.env.NEXT_PUBLIC_ENV === "production";

  useEffect(() => {
    gtm.initialize(gtmConfig);
  }, []);

  if (typeof window !== "undefined") {
    try {
      window.Paddle.Setup({
        vendor: Number(process.env.NEXT_PUBLIC_PADDLE_VENDOR_ID),
        eventCallback: function (data) {
          if (data.event === "Checkout.Complete") {
            // Data specifics on the event
          } else if (data.event === "Checkout.Close") {
            // Data specifics on the event
          }
        },
      });

      if (!isProduction) {
        window.Paddle.Environment.set("sandbox");
      }
    } catch (error) {
      console.error("Error setting up Paddle:", error);
    }
  }

  const site = "https://seowind.io/app";
  const canonicalURL = site + useRouter().pathname;

  return (
    <CacheProvider value={emotionCache}>
      <Head>
        <title>SEOwind.io</title>
        <meta name="viewport" content="initial-scale=1, width=device-width" />
        <link rel="canonical" href={canonicalURL} />
      </Head>
      <Script
        id="stripe-js"
        src="https://bernii.github.io/gauge.js/dist/gauge.min.js"
        strategy="afterInteractive"
        onError={(e) => {
          console.error("Error loading gauge.js script:", e);
        }}
      />
      <Script
        id="paddle-js"
        src="https://cdn.paddle.com/paddle/paddle.js"
        strategy="beforeInteractive"
        onError={(e) => {
          console.error("Error loading paddle.js script:", e);
        }}
      />
      <DndProvider backend={HTML5Backend}>
        <ReduxProvider store={store}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <AuthProvider>
              <SettingsProvider>
                <SettingsConsumer>
                  {({ settings }) => (
                    <ThemeProvider
                      theme={createTheme({
                        direction: settings.direction,
                        responsiveFontSizes: settings.responsiveFontSizes,
                        mode: settings.theme,
                      })}
                    >
                      <RTL direction={settings.direction}>
                        <CssBaseline />
                        <Toaster position="top-center" />
                        <SnackbarProvider />
                        <AuthConsumer>
                          {(auth) =>
                            !auth.isInitialized ? (
                              <SplashScreen />
                            ) : (
                              getLayout(<Component {...pageProps} />)
                            )
                          }
                        </AuthConsumer>
                      </RTL>
                    </ThemeProvider>
                  )}
                </SettingsConsumer>
              </SettingsProvider>
            </AuthProvider>
          </LocalizationProvider>
        </ReduxProvider>
      </DndProvider>
    </CacheProvider>
  );
};

export default App;
